import { computed, Injectable, Signal, signal, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { MatDateFormats } from '@angular/material/core';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { SystemCountry } from '@dibsondance/shared-types';
import { LoggerService } from '@dibsondance/shared-logging';

import { TranslationLoaderService } from './translation-loader.service';
import { DateTimeFormatOptions } from './date-format.options';
import { EnvironmentService } from '@dibsondance/shared-environment-base';

export enum AppLanguage {
  Pl = 'pl-PL',
  En = 'en-GB',
  De = 'de-DE',
  Lt = 'lt-LT',
  Fr = 'fr-FR',
  Es = 'es-ES',
  It = 'it-IT',
  Pt = 'pt-PT',
  Nl = 'nl-NL',
  Cs = 'cs-CZ',
  Sk = 'sk-SK',
  Hu = 'hu-HU',
  Ro = 'ro-RO',
  Bg = 'bg-BG',
  Hr = 'hr-HR',
  Sl = 'sl-SI',
  El = 'el-GR',
  Et = 'et-EE',
  Lv = 'lv-LV',
  Fi = 'fi-FI',
  Sv = 'sv-SE',
  Da = 'da-DK',
  Mt = 'mt-MT',
}

export const MY_DATE_FORMATS_EN_GB: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const MY_DATE_FORMATS_PL_PL: MatDateFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const FULL_DATE_TIME_FORMAT_EN_GB: DateTimeFormatOptions = {
  weekday: 'long', // Full name of the day of the week
  year: 'numeric', // Full year (e.g., 2025)
  month: 'long', // Full month name (e.g., March)
  day: 'numeric', // Numeric day (e.g., 28)
  hour: '2-digit', // 2-digit hour (e.g., 10)
  minute: '2-digit', // 2-digit minute (e.g., 30)
  timeZoneName: 'short', // Short time zone (e.g., GMT)
};

const FULL_DATE_TIME_FORMAT_PL_PL: DateTimeFormatOptions = {
  weekday: 'long', // Full name of the day of the week
  year: 'numeric', // Full year (e.g., 2025)
  month: 'long', // Full month name (e.g., marzec)
  day: 'numeric', // Numeric day (e.g., 28)
  hour: '2-digit', // 2-digit hour (e.g., 10)
  minute: '2-digit', // 2-digit minute (e.g., 30)
  timeZoneName: 'short', // Short time zone (e.g., GMT+2)
};

// Short date format: "Sat, 15 Mar, 10:30"
const SHORT_DATE_TIME_FORMAT_EN_GB: DateTimeFormatOptions = {
  weekday: 'short', // Short name (e.g., Sat)
  month: 'short', // Short month name (e.g., Mar)
  day: 'numeric', // Numeric day (e.g., 15)
  hour: '2-digit', // 2-digit hour (e.g., 10)
  minute: '2-digit', // 2-digit minute (e.g., 30)
};

// Short date format: "sob., 15 mar, 10:30"
const SHORT_DATE_TIME_FORMAT_PL_PL: DateTimeFormatOptions = {
  weekday: 'short', // Short name (e.g., sob.)
  month: 'short', // Short month name (e.g., mar)
  day: 'numeric', // Numeric day (e.g., 15)
  hour: '2-digit', // 2-digit hour (e.g., 10)
  minute: '2-digit', // 2-digit minute (e.g., 30)
};

export const MY_DATE_FORMATS_DE_DE: MatDateFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const FULL_DATE_TIME_FORMAT_DE_DE: DateTimeFormatOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
};

const SHORT_DATE_TIME_FORMAT_DE_DE: DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

export const MY_DATE_FORMATS_LT_LT: MatDateFormats = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'YYYY MMMM',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY MMMM',
  },
};

const FULL_DATE_TIME_FORMAT_LT_LT: DateTimeFormatOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
};

const SHORT_DATE_TIME_FORMAT_LT_LT: DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

@Injectable({
  providedIn: 'root',
})
export class I18nHelper {
  private static readonly LANGUAGE_STORAGE_KEY = 'dod-host-language';
  private static translateServices: TranslateService[] = [];
  private readonly currentLanguage = signal<AppLanguage>(AppLanguage.Pl);
  private readonly logger = inject(LoggerService);
  private readonly platformId = inject(PLATFORM_ID);

  private static loadedTranslations = new BehaviorSubject<Set<string>>(
    new Set()
  );

  constructor() {
    // During SSR each request creates a new root injector and a new I18nHelper instance.
    // The previous request's TranslateService references are now stale (destroyed injector).
    // Calling .use() on them triggers async HTTP requests that crash with NG0205.
    // Clear all stale references so only the current request's services are used.
    if (isPlatformServer(this.platformId)) {
      I18nHelper.translateServices = [];
      I18nHelper.loadedTranslations = new BehaviorSubject<Set<string>>(new Set());
      TranslationLoaderService.clearOngoingRequests();
    }

    // Restore persisted language preference (browser only).
    // DOD-564 follow-up — when no stored preference exists (first visit),
    // derive an initial language from `navigator.language` so a German
    // browser doesn't get the hard-coded Polish default. Falls through
    // to AppLanguage.Pl when the browser's language isn't supported.
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(I18nHelper.LANGUAGE_STORAGE_KEY);
      if (stored && Object.values(AppLanguage).includes(stored as AppLanguage)) {
        this.currentLanguage.set(stored as AppLanguage);
      } else {
        // Always English fallback for unsupported browser locales —
        // never leave the user on the hard-coded Polish default when
        // their browser said something else.
        this.currentLanguage.set(I18nHelper.detectBrowserLanguage(navigator));
      }
    }
  }

  /**
   * DOD-564 follow-up — match the browser's preferred language to one of
   * the 23 supported `AppLanguage` locales. Looks at `navigator.language`
   * first, then `navigator.languages[0]` as a fallback. Matches by the
   * 2-letter ISO-639 prefix (so `en-US` resolves to `AppLanguage.En`
   * even though our locale is `en-GB`). Falls back to AppLanguage.En
   * when the browser language isn't in the supported set OR navigator is
   * unavailable (SSR) — English is the safe default for international
   * visitors so an unrecognised browser locale doesn't park the user
   * on Polish.
   */
  static detectBrowserLanguage(navigator: Navigator | null): AppLanguage {
    if (!navigator) return AppLanguage.En;
    const raw =
      navigator.language ||
      (navigator.languages && navigator.languages[0]) ||
      '';
    if (!raw) return AppLanguage.En;
    const prefix = raw.split('-')[0].toLowerCase();
    const match = Object.values(AppLanguage).find(
      (locale) => locale.split('-')[0].toLowerCase() === prefix,
    );
    return match ?? AppLanguage.En;
  }

  static createTranslateHttpLoader(modulePath: string) {
    return (httpClient: HttpClient, environmentService: EnvironmentService) => {
      return new TranslationLoaderService(
        httpClient,
        modulePath,
        environmentService
      );
    };
  }

  static appLanguageToSystem(appLanguage: AppLanguage): SystemCountry {
    // Extract short code from locale (e.g. 'pl-PL' → 'pl')
    const code = appLanguage.split('-')[0].toLowerCase();
    const systemCountry = Object.values(SystemCountry).find(sc => sc === code);
    if (!systemCountry) throw new Error(`Unknown language: ${appLanguage}`);
    return systemCountry;
  }

  get currentLang(): Signal<AppLanguage> {
    return this.currentLanguage;
  }

  get currentSystemLang(): Signal<SystemCountry> {
    return computed(() =>
      I18nHelper.appLanguageToSystem(this.currentLanguage())
    );
  }

  fullDateFormat(_appLanguage: AppLanguage): DateTimeFormatOptions {
    // All locales use the same structure — Intl.DateTimeFormat handles
    // locale-specific formatting (day/month order, names) automatically
    return FULL_DATE_TIME_FORMAT_EN_GB;
  }

  shortDateFormat(_appLanguage: AppLanguage): DateTimeFormatOptions {
    return SHORT_DATE_TIME_FORMAT_EN_GB;
  }

  // Pending translations to apply to new TranslateService instances when they register
  private static pendingTranslations: Map<string, Record<string, string>> = new Map();

  registerTranslateService(translateService: TranslateService): void {
    I18nHelper.translateServices.push(translateService);
    this.init(translateService);

    // Apply pending translations ONLY for current language.
    // Setting translations for other languages would mark them as "loaded" in ngx-translate,
    // preventing the HTTP loader from fetching the full translation file when switching languages.
    const currentLang = this.currentLanguage();
    const pending = I18nHelper.pendingTranslations.get(currentLang);
    if (pending) {
      translateService.setTranslation(currentLang, pending, true);
    }
  }

  /**
   * Set translation key-value pairs on ALL registered TranslateService instances
   * (including isolated module instances). Also stores for future lazy-loaded instances.
   *
   * Only applies immediately to the CURRENT language to avoid poisoning the ngx-translate
   * cache for languages that haven't been fully loaded yet.
   */
  static setTranslationOnAll(locale: string, translations: Record<string, string>): void {
    // Always store for future use (lazy-loaded modules + language switches)
    const existing = I18nHelper.pendingTranslations.get(locale) ?? {};
    I18nHelper.pendingTranslations.set(locale, { ...existing, ...translations });

    // Only apply immediately if this is the current language
    // (other languages will get merged after the loader fetches them in changeLanguage)
    const currentLang = I18nHelper.translateServices.length > 0
      ? I18nHelper.translateServices[0].currentLang || I18nHelper.translateServices[0].defaultLang
      : null;

    if (currentLang === locale) {
      I18nHelper.translateServices.forEach((service) => {
        service.setTranslation(locale, translations, true);
      });
    }
  }

  changeLanguage(language: AppLanguage) {
    this.currentLanguage.set(language);

    // Persist language preference (browser only)
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(I18nHelper.LANGUAGE_STORAGE_KEY, language);
    }

    const pending = I18nHelper.pendingTranslations.get(language);

    I18nHelper.translateServices.forEach((translateService) => {
      translateService.use(language).subscribe(() => {
        // Merge pending translations (e.g., backend validator messages)
        // after the full translation file has been loaded
        if (pending) {
          translateService.setTranslation(language, pending, true);
        }
      });
      translateService.setDefaultLang(language);
    });
  }

  /**
   * Mark a module translation as loaded
   * Called from TranslationLoaderService when translation completes
   */
  static markTranslationLoaded(moduleName: string, language: string): void {
    const cacheKey = `${moduleName}-${language}`;
    const currentLoaded = I18nHelper.loadedTranslations.value;
    const newLoaded = new Set(currentLoaded);
    newLoaded.add(cacheKey);
    I18nHelper.loadedTranslations.next(newLoaded);

    // Use try/catch for static context logger access
    try {
      const logger = inject(LoggerService);
      logger.debug(`Translation marked as loaded: ${cacheKey}`, undefined, 'I18nHelper');
    } catch {
      // Fallback only in static context when inject fails
    }
  }

  /**
   * Wait for module translations to be loaded - simple BehaviorSubject approach
   * @param moduleName The module name (e.g., 'tickets', 'portal')
   * @param timeout Optional timeout in milliseconds (default 10000ms)
   * @returns Observable<boolean> that emits true when loaded or after timeout
   */
  waitForModuleTranslations(
    moduleName: string,
    timeout = 10000
  ): Observable<boolean> {
    const currentLang = this.currentLanguage();
    const cacheKey = `${moduleName}-${currentLang}`;

    // Check if already loaded
    if (I18nHelper.loadedTranslations.value.has(cacheKey)) {
      this.logger.debug(`Translations already loaded: ${cacheKey}`, undefined, 'I18nHelper');
      return of(true);
    }

    this.logger.debug(`Waiting for translations: ${cacheKey}`, undefined, 'I18nHelper');

    // Wait for the translation to be loaded with timeout
    return new Observable<boolean>((observer) => {
      let completed = false;

      // Subscribe to loaded translations
      const subscription = I18nHelper.loadedTranslations
        .pipe(
          map((loadedSet) => loadedSet.has(cacheKey)),
          filter((isLoaded) => isLoaded),
          take(1)
        )
        .subscribe(() => {
          if (!completed) {
            completed = true;
            this.logger.debug(`Translation loaded: ${cacheKey}`, undefined, 'I18nHelper');
            observer.next(true);
            observer.complete();
          }
        });

      // Set up timeout
      const timeoutSub = timer(timeout).subscribe(() => {
        if (!completed) {
          completed = true;
          this.logger.warn(
            `Translation timeout (${timeout}ms) for ${cacheKey}, proceeding anyway`,
            undefined,
            'I18nHelper'
          );
          observer.next(true);
          observer.complete();
        }
      });

      // Cleanup
      return () => {
        subscription.unsubscribe();
        timeoutSub.unsubscribe();
      };
    });
  }

  currentFormat(): MatDateFormats {
    const DATE_FORMAT_MAP: Record<string, MatDateFormats> = {
      [AppLanguage.En]: MY_DATE_FORMATS_EN_GB,
      [AppLanguage.Lt]: MY_DATE_FORMATS_LT_LT,
    };
    // Most EU locales use DD.MM.YYYY (same as DE/PL format).
    // EN uses DD/MM/YYYY, LT uses YYYY-MM-DD.
    return DATE_FORMAT_MAP[this.currentLanguage()] ?? MY_DATE_FORMATS_DE_DE;
  }

  private init(translateService: TranslateService): void {
    translateService.addLangs(Object.values(AppLanguage));
    const currentLanguage = this.currentLanguage();
    translateService.use(currentLanguage);
    translateService.setDefaultLang(currentLanguage);
  }
}
